import React from "react";
import { useReveal } from "../../hooks/useReveal";
import { Ornament } from "../Ornaments";

const cards = [
  {
    label: "HRV Coherence",
    body: "Heart rate variability is the primary measurable marker of nervous system coherence. Higher coherence means greater capacity to adapt, regulate, and grow.",
  },
  {
    label: "Fascial Tensegrity",
    body: "The fascial network transmits mechanical and electrical signals throughout the body. Intentional movement reorganizes this network at the cellular level.",
  },
  {
    label: "Neural Synchrony",
    body: "Synchronized oscillation across brain regions is the biological basis of integration — and the mechanism through which conscious self-cultivation becomes embodied.",
  },
];

const CoherenceCard = ({ label, body, index }) => {
  const rv = useReveal();
  return (
    <div
      ref={rv.ref}
      className={`card-gold-left reveal ${rv.inView ? "in-view" : ""}`}
      style={{ transitionDelay: `${index * 90}ms` }}
      data-testid={`coherence-card-${index}`}
    >
      <p
        className="font-heading"
        style={{
          color: "var(--gold)",
          fontSize: "0.72rem",
          letterSpacing: "0.32em",
          marginBottom: "0.6rem",
        }}
      >
        {label}
      </p>
      <p className="font-body" style={{ color: "var(--text)", fontSize: "0.98rem", lineHeight: 1.75 }}>
        {body}
      </p>
    </div>
  );
};

export const Coherence = () => {
  const head = useReveal();
  const left = useReveal();
  return (
    <section
      id="biology"
      className="section bg-elevated-section"
      data-testid="coherence-section"
      style={{ position: "relative" }}
    >
      <div className="gold-glow" style={{ width: 700, height: 700, top: -300, right: -200 }} />

      <div className="section-inner">
        <div ref={head.ref} className={`reveal ${head.inView ? "in-view" : ""}`} style={{ maxWidth: 780, marginBottom: "3.5rem" }}>
          <Ornament />
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            The Biology
          </p>
          <h2 className="section-title" data-testid="coherence-title">
            Coherence Is Not <br /> a Metaphor
          </h2>
        </div>

        <div className="two-col">
          <div ref={left.ref} className={`reveal ${left.inView ? "in-view" : ""}`}>
            <p className="lead" data-testid="coherence-prose">
              When biological systems synchronize — heart rhythm, neural oscillation, fascial
              tensegrity, breath — the body enters coherence. In coherence, inflammation
              decreases, cognitive clarity increases, emotional regulation becomes available,
              and the nervous system can reorganize old patterns.
            </p>
            <p className="lead" style={{ marginTop: "1.5rem" }}>
              This is the biological foundation of self-programming: you cannot consciously
              cultivate who you are from a dysregulated system. <span style={{ color: "var(--gold-bright)" }}>Coherence is the prerequisite.</span>
            </p>
            <p className="lead lead-muted" style={{ marginTop: "1.5rem" }}>
              Movement, accurately applied, is how we restore it.
            </p>
            <p className="italic-quote" data-testid="coherence-riff-quote" style={{ marginTop: "1.75rem", color: "var(--text-muted)", fontSize: "0.98rem", lineHeight: 1.8, borderLeft: "2px solid rgba(201,168,76,0.35)", paddingLeft: "1.25rem" }}>
              &ldquo;It&rsquo;s kind of like jazz — you play around until you find a riff.
              Once you&rsquo;ve got it, you repeat it. Then it turns into a pattern.&rdquo;
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            {cards.map((c, i) => (
              <CoherenceCard key={c.label} label={c.label} body={c.body} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
